/**
 * Header superior. La navegación de secciones vive en AppSidebar; acá quedan
 * solo el branding (logo + nombre del policlínico) y el menú de usuario, más el
 * botón que abre el drawer de navegación en móvil.
 */
import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, User, ChevronDown, Menu } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { useTenantBranding } from "../auth/TenantBrandingProvider";
import { tenantLogoUrl } from "../api/tenant_branding";

interface Props {
  /** Prefijo de las rutas del policlínico. Ver `useBase()`. */
  base: string;
  onOpenMobileNav: () => void;
  /** AppLayout lo usa para devolver el foco acá al cerrar el drawer. */
  mobileNavButtonRef?: React.RefObject<HTMLButtonElement>;
}

export function AppHeader({ base, onOpenMobileNav, mobileNavButtonRef }: Props) {
  const { me, logout } = useAuth();
  const { branding, isLoading: brandingLoading } = useTenantBranding();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  // Apellidos y nombre, que es como se nombra a alguien en una institución.
  const displayName = me?.nombre_para_mostrar || me?.email || "??";
  // Iniciales del apellido: "Rodrigues Campos, Tania" → RO.
  const initials = displayName.replace(/[^\p{L}]/gu, "").slice(0, 2).toUpperCase();
  // Mientras el branding viaja no inventamos nombre: "Plataforma · Tenant #253"
  // es peor que un espacio en blanco — le dice al usuario que está en un
  // sistema ajeno justo en la pantalla que debería sentir suya.
  const tenantLabel = branding?.nombre_comercial ?? (brandingLoading ? "" : "—");

  async function handleLogout() {
    await logout();
    navigate(`${base}/login`, { replace: true });
  }

  // El logo del policlínico; si no subió ninguno, su inicial.
  const renderLogo = () => {
    if (branding?.has_logo && branding.slug) {
      return (
        <img
          src={tenantLogoUrl()}
          alt=""
          className="h-8 w-8 shrink-0 object-contain"
        />
      );
    }
    return (
      <div
        aria-hidden="true"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white"
        style={{ backgroundColor: "var(--color-primary)" }}
      >
        {(branding?.nombre_comercial ?? "P").slice(0, 1).toUpperCase()}
      </div>
    );
  };

  // La base es "" y un href vacío no navega a ningún sitio: la raíz es "/".
  const homeHref = base || "/";
  const homeLabel = branding?.nombre_comercial ?? (brandingLoading ? "" : "—");

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-4 sm:px-6">
      <button
        ref={mobileNavButtonRef}
        onClick={onOpenMobileNav}
        className="-ml-1 shrink-0 rounded-lg p-2 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400 lg:hidden"
        aria-label="Abrir navegación"
        aria-controls="nav-principal"
      >
        <Menu className="h-5 w-5" />
      </button>

      <Link
        to={homeHref}
        className="flex min-w-0 items-center gap-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
      >
        {renderLogo()}
        <span className="truncate text-sm font-semibold text-slate-900">{homeLabel}</span>
      </Link>

      <div className="relative ml-auto shrink-0 pl-4" ref={menuRef}>
        <button
          onClick={() => setMenuOpen((o) => !o)}
          className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          title={`${displayName} — ${tenantLabel}`}
        >
          <div
            aria-hidden="true"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            {initials}
          </div>
          <div className="hidden max-w-[200px] text-left text-xs leading-tight sm:block">
            <div className="truncate font-medium text-slate-900">{displayName}</div>
            <div className="truncate text-slate-400">{tenantLabel}</div>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
        </button>

        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-full z-30 mt-1 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          >
            <Link
              to={`${base}/me`}
              role="menuitem"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <User className="h-4 w-4" aria-hidden="true" />
              Mi perfil
            </Link>
            <div className="my-1 border-t border-slate-100" />
            <button
              role="menuitem"
              onClick={handleLogout}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Cerrar sesión
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
